import { useRef, useState, useEffect, useCallback } from 'react';

import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
  Box,
  Card,
  Alert,
  Stack,
  Button,
  Select,
  Tooltip,
  MenuItem,
  CardHeader,
  InputLabel,
  FormControl,
} from '@mui/material';

import useLocales from 'src/hooks/useLocales';
import useNotifications from 'src/hooks/useNotifications';

import { reportWorkflowError } from 'src/services/workflow/workflowError';

import { getWorkflowDefinitions } from 'src/services/workflow/workflowDefinitionService';
import {
  bindWorkflow,
  unbindWorkflow,
  getWorkflowBinding,
} from 'src/services/workflow/workflowBindingService';
import {
  WorkflowSubjectType,
  WorkflowSubjectStateDto,
  WorkflowDefinitionSummaryDto,
} from 'src/@types/workflow/types';
import LoadingSpinner from 'src/components/LoadingSpinner';

import WorkflowLoadError from './WorkflowLoadError';

type WorkflowBindingPanelProps = {
  subjectType: WorkflowSubjectType;
  subjectId: number;
  title?: string;
  readOnly?: boolean;
  embedded?: boolean;
  onStateChange?: (state: WorkflowSubjectStateDto | null) => void;
  children?: React.ReactNode;
};

export default function WorkflowBindingPanel({
  subjectType,
  subjectId,
  title,
  readOnly,
  embedded,
  onStateChange,
  children,
}: WorkflowBindingPanelProps) {
  const { t } = useLocales();
  const { enqueueSuccess, enqueueError } = useNotifications();

  const [workflowState, setWorkflowState] = useState<WorkflowSubjectStateDto | null>(null);
  const [definitions, setDefinitions] = useState<WorkflowDefinitionSummaryDto[]>([]);
  const [selectedDefinitionNumber, setSelectedDefinitionNumber] = useState<number | ''>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);

  const currentLoad = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    currentLoad.current?.abort();

    const running = new AbortController();
    currentLoad.current = running;

    setIsLoading(true);
    setHasLoadFailed(false);

    try {
      const [current, available] = await Promise.all([
        getWorkflowBinding(subjectType, subjectId, running.signal),
        getWorkflowDefinitions(running.signal),
      ]);

      setWorkflowState(current);
      setDefinitions(available.filter((definition) => definition.isActive));
      setSelectedDefinitionNumber(current.workflowDefinitionNumber ?? '');
      onStateChange?.(current);
    } catch (error) {
      if (running.signal.aborted) {
        return;
      }
      setHasLoadFailed(true);
      reportWorkflowError(error, 'workflows.binding.loadFailed', enqueueError);
    } finally {
      if (!running.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [subjectType, subjectId, enqueueError, onStateChange]);

  useEffect(() => {
    load();

    return () => currentLoad.current?.abort();
  }, [load]);

  const handleBind = async (definitionNumber: number) => {
    setIsSaving(true);
    try {
      const next = await bindWorkflow(subjectType, subjectId, definitionNumber);
      setWorkflowState(next);
      onStateChange?.(next);
      enqueueSuccess('workflows.binding.saved');
    } catch (error) {
      reportWorkflowError(error, 'workflows.binding.saveFailed', enqueueError);
      await load();
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnbind = async () => {
    setIsSaving(true);
    try {
      await unbindWorkflow(subjectType, subjectId);
      enqueueSuccess('workflows.binding.removed');
      await load();
    } catch (error) {
      reportWorkflowError(error, 'workflows.binding.removeFailed', enqueueError);
    } finally {
      setIsSaving(false);
    }
  };

  const spinner = (
    <Box sx={{ py: 5 }}>
      <LoadingSpinner size={40} />
    </Box>
  );

  if (isLoading) {
    return embedded ? spinner : <Card>{spinner}</Card>;
  }

  const loadFailure = (
    <WorkflowLoadError
      messageLocale="workflows.binding.loadFailed"
      onRetry={load}
      sx={{ m: embedded ? 0 : 3 }}
    />
  );

  if (hasLoadFailed) {
    return embedded ? loadFailure : <Card>{loadFailure}</Card>;
  }

  const isLocked = readOnly || isSaving || workflowState?.isRunning === true;
  const hasOwnBinding = workflowState?.isBound === true && !workflowState.isInherited;

  const body = (
    <Stack spacing={2} sx={{ p: embedded ? 0 : 3 }}>
      {workflowState?.isInherited && (
        <Alert severity="info">
          {t(`workflows.binding.inheritedFrom.${workflowState.boundAt}`, {
            name: workflowState.boundAtName,
          })}{' '}
          {t('workflows.binding.overrideHint')}
        </Alert>
      )}

      {workflowState?.isRunning && (
        <Alert severity="warning">{t('workflows.binding.runningLocked')}</Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'center' }}>
        <FormControl fullWidth size="small" disabled={isLocked}>
          <InputLabel>{t('workflows.definitionFieldLabel')}</InputLabel>
          <Select
            label={t('workflows.definitionFieldLabel')}
            value={selectedDefinitionNumber}
            onChange={(event) => {
              const next = event.target.value as number;
              setSelectedDefinitionNumber(next);
              handleBind(next);
            }}
          >
            {definitions.length === 0 && (
              <MenuItem value="" disabled>
                {t('workflows.noDefinitions')}
              </MenuItem>
            )}

            {definitions.map((definition) => (
              <MenuItem key={definition.definitionNumber} value={definition.definitionNumber}>
                {definition.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {hasOwnBinding && (
          <Tooltip title={t('workflows.binding.remove')}>
            <Button
              color="inherit"
              startIcon={<LinkOffIcon />}
              disabled={isLocked}
              onClick={handleUnbind}
              sx={{ flexShrink: 0 }}
            >
              {t('workflows.binding.remove')}
            </Button>
          </Tooltip>
        )}
      </Stack>

      {subjectType === 'document' && children}
    </Stack>
  );

  if (embedded) {
    return body;
  }

  return (
    <Card>
      <CardHeader
        title={title ?? t('workflows.binding.title')}
        subheader={t(`workflows.binding.subheader.${subjectType}`)}
      />
      {body}
    </Card>
  );
}
